/* the following is a modified version of RemoteDataSource from @airnauts/ng-components */

import { SortDirection } from "@/core/graphql/graphql";
import { InteractableDataSource, PageEvent } from "@airnauts/ng-components";
import { ITableDataSource } from "@airnauts/ng-components/table/table.component";
import { Observable, BehaviorSubject, Subject, combineLatest } from "rxjs";
import { debounceTime, map, startWith, switchMap, tap } from "rxjs/operators";
import { StackPaginatorComponent } from "../stack-paginator/stack-paginator.component";

export type QueryTabularResult<T> = {
  items: T[],
  meta: { nextPageCursor?: string | null },
};

export type DataSourceQueryParams = {
  searchTerm: string,
  sort: SortInput[],
  pageCursor: string,
}

export type DataSourceQuery<T> = (obs: DataSourceQueryParams) => Observable<QueryTabularResult<T>>;

export type SortInput = {
  sortField: string,
  sortDirection: SortDirection
}

export class CustomDataSource<T> extends InteractableDataSource<T> implements ITableDataSource<T> {
  private readonly _sortSubject: Subject<SortInput[]> = new Subject<SortInput[]>();
  private readonly _paginationSubject = new BehaviorSubject<string>(this._paginator.value);
  private readonly _searchSubject: Subject<string> = new Subject<string>();
  private readonly _fetchTriggerSubject: Subject<void> = new Subject<void>();
  private _totalItemsSubject = new BehaviorSubject<number>(0);
  private readonly _customLoading = new BehaviorSubject<boolean>(false);
  public readonly totalItems$ = this._totalItemsSubject.asObservable();
  public readonly customLoading$ = this._customLoading.asObservable();

  public constructor(
    private readonly _dService: DataSourceQuery<T>,
    private readonly _paginator: StackPaginatorComponent,
  ) {
    super();
  }

  public connect(): Observable<T[]> {
    return this._getFetchingPipeline()
      .pipe(map(x => x.items));
  }

  public disconnect(): void {
  }

  public setSearch(search: any): void {
    this._searchSubject.next(search);
  }

  public setSort(sort: any): void {
    const { active, direction } = sort;
    if (direction === "") {
      this._sortSubject.next([]);
      return;
    }
    this._sortSubject.next([{ sortField: active, sortDirection: direction }]);
  }

  public setPaginator(paginationDetails: PageEvent): void {
    throw new Error("Method not supported");
  }

  public setCursorPaginator(value: string) {
    this._paginationSubject.next(value);
  }

  public setLoading(loading: boolean): void {
    this._loading.next(false);
    this._customLoading.next(loading);
  }

  public refresh(): void {
    this._fetchTriggerSubject.next();
  }

  private _getFetchingPipeline(): Observable<QueryTabularResult<T>> {
    const sortChange$ = this._sortSubject.pipe(
      startWith([]),
    );

    const pageChange$ = this._paginationSubject.pipe(
      startWith(''),
    );

    const searchChange$: Observable<string> = this._searchSubject.pipe(
      debounceTime(500),
      startWith(''),
    );

    const trigger$ = this._fetchTriggerSubject.pipe(
      startWith(null),
    );

    return combineLatest([
      sortChange$,
      pageChange$,
      searchChange$,
      trigger$,
    ]).pipe(
      tap(() => this.setLoading(true)),
      switchMap(([sort, page, search, _]) => this._dService({
        searchTerm: search,
        sort: sort,
        pageCursor: page,
      })),
      tap(() => this.setLoading(false)),
      tap((x) => this._paginator.update({ nextCursor: x.meta.nextPageCursor ?? null })),
    );
  }
}
